import React from "react"
import { Form, Col, Image, Spinner } from "react-bootstrap"
import { Typeahead } from "react-bootstrap-typeahead"
import { BehaviorSubject, concat, of, Subject, Subscription } from "rxjs"
import { filter, map, switchMap } from "rxjs/operators"
import SelectServiceTogglePanel from "./SelectServiceTogglePanel"
import { ServiceDataModel, EmptyServiceData } from "../OrderModels"
import dotImage from "../../../assets/images/dot-img.png"
import SelectService, { SelectServiceProps, SelectServiceState } from "./SelectService"

import TestToBePerformGetObservable from "../../../api/LabService/TestToBePerformGet"
import LabAccountsGetObservable from "../../../api/LabService/LabAccountsGet"
import LabAccount from "../../../api/LabService/LabServiceModel/LabAccount"
import TestToBePerform from "../../../api/LabService/LabServiceModel/TestToBePerform"
import { ServiceTypeCode } from "../../../api/Administration/Model/ServiceType"
import Service, { ServiceByCode, IsServiceCodePOCT} from "../../../api/Administration/Model/Service"
import TestToBePerformWithCompanyName from "../../../api/LabService/LabServiceModel/TestToBePerformWithCompanyName"
import { SampleType } from "../../../api/LookupCodes/SampleType"
import SelectSpecimenCollection from "./SelectSpecimenCollection"
import SelectObserved from "./SelectObserved"
import RegulatoryModeControl from "../RegulatoryModeControl"
import DrugServicesPanel from "./DrugServicesPanel"
import { IsServiceFLEX, IsServicePOCT } from "../../../api/Administration/DrugService"
import { ServiceTests } from "../../../api/Administration/Model/ServiceTests"
import { ServiceCode } from "../../../api/LookupCodes/ServiceCode"
import { DefaultLookupCode } from "../../../api/LookupCodes/DefaultLookupCode"

export interface SelectDrugTestProps extends SelectServiceProps {
  company?: number
  location?: number
  canAddPOCT: boolean
  canAddFLEX: boolean
  isObservedMandatory: boolean
  sampleType?: SampleType
}

interface SelectDrugTestState extends SelectServiceState {
  labAccountsLoading: boolean
  testsLoading: boolean
  labAccounts: LabAccount[]
  availableTests: TestToBePerform[]
  tests: TestToBePerform[]
  serviceTests: ServiceTests
  labAccountCompanyName?: string
  isAllServicesDisabled: boolean
}

export default class SelectDrugTest extends SelectService<SelectDrugTestProps, SelectDrugTestState> {
  defaultService: ServiceDataModel = {
    ...EmptyServiceData,
    serviceTypeCode: ServiceTypeCode.Drug,
    serviceCode: ServiceCode.None,
    observedCollection: false,
  }
  state: SelectDrugTestState = {
    ...this.defaultService,
    labAccountsLoading: false,
    testsLoading: false,
    labAccounts: [],
    availableTests: [],
    tests: [],
    serviceTests: {} as ServiceTests,
    isDOT: this.props.isDot,
    reqSpecimenCollection: this.props.isDot ? "split" : "single",
    isAllServicesDisabled: true,
  }

  subscriptions = new Subscription()
  company$ = new BehaviorSubject<number>(0)
  labAccount$ = new Subject<LabAccount>()

  componentDidMount() {
    this.subscriptions.add(
      this.company$
        .pipe(
          switchMap((company: number) =>
            !company
              ? of({ isLoading: false })
              : concat(
                  of({ isLoading: true }),
                  LabAccountsGetObservable({
                    SearchFor: "",
                    SearchBy: "Account Name",
                    CompanyID: company,
                    RegionID: 0,
                    LocationID: this.props.location || 0,
                    DOT: !!this.state.isDOT,
                    IsMarketPlace: true,
                  }).pipe(map((data) => ({ isLoading: false, result: data })))
                )
          )
        )
        .subscribe((data: { isLoading: boolean; result?: LabAccount[] }) => {
          this.setState({
            labAccountsLoading: data.isLoading,
            labAccounts: data.result || [],
          })
          const selectedLabAccount = this.props.labAccountNumber
            ? data.result?.find((item) => item.LabAccount === this.props.labAccountNumber)
            : undefined
          this.labAccount$.next(selectedLabAccount)
        })
    )

    this.subscriptions.add(
      this.labAccount$.subscribe((selected) =>
        this.setState({
          labAccount: selected?.LabCode || "",
          labAccountObj: selected,
          availableTests: [],
          tests: [],
          testToPerform: "",
          testToPerformObj: undefined,
          labAccountCompanyName: undefined,
          isAllServicesDisabled: true,
          serviceTests: {} as ServiceTests,
        })
      )
    )

    this.subscriptions.add(
      this.labAccount$
        .pipe(
          filter((selected) => !!selected),
          switchMap((selected: LabAccount) =>
            concat(
              of({ isLoading: true }),
              TestToBePerformGetObservable({
                LabAccountNumber: selected.LabAccount,
                VendorID: selected.LabClientMemberId,
                IsDOT: !!this.state.isDOT,
                IsPoct: IsServiceCodePOCT(this.state.serviceCode),
                IsMarketPlace: true,
              }).pipe(map((data) => ({ isLoading: false, result: data })))
            )
          )
        )
        .subscribe((data: { isLoading: boolean; result?: TestToBePerformWithCompanyName }) => {
          const availableTests = data.result?.Panels || []
          const companyName = data.result?.CompanyName
          const labAccount = this.state.labAccountObj

          if (!!companyName && companyName.length > 0 && labAccount) {
            const newLabAccount = {
              ...labAccount,
              Description: labAccount.Description.replace("Undefined", companyName),
            }
            if (newLabAccount.Description !== labAccount.Description) {
              const index = this.state.labAccounts.indexOf(labAccount)
              const newLabAccountsArray: LabAccount[] = [
                ...this.state.labAccounts.slice(0, index),
                newLabAccount,
                ...this.state.labAccounts.slice(index + 1),
              ]

              this.setState({ labAccountObj: newLabAccount, labAccounts: newLabAccountsArray })
            }
          }

          const serviceTests = this.groupTestsByService(availableTests)

          const tests = Object.values(serviceTests).reduce((acc, values) => {
            return acc.concat(values)
          }, [])

          this.setState(
            {
              testsLoading: data.isLoading,
              availableTests: tests,
              labAccountCompanyName: data.result?.CompanyName,
              tests: [],
              testToPerform: "",
              testToPerformObj: undefined,
              serviceTests,
            },
            () => this.selectServiceByDefault()
          )
        })
    )

    if (this.props.company) {
      this.company$.next(this.props.company)
    }
  }

  componentWillUnmount() {
    this.subscriptions.unsubscribe()
  }

  componentDidUpdate(prevProps: SelectDrugTestProps) {
    if (
      this.props.company &&
      (this.props.company !== prevProps.company || (this.props.location && this.props.location !== prevProps.location))
    ) {
      this.company$.next(this.props.company)
    }
  }

  setInitialState = () => this.setState({ ...this.defaultService, labAccountCompanyName: undefined })

  filterTests = (tests: TestToBePerform[], service?: Service, sampleType?: SampleType) => {
    const { orderableServices } = this.props
    const { serviceCode } = this.state

    const selectedService = service || ServiceByCode(orderableServices, serviceCode)
    const isPoct = IsServicePOCT(selectedService)
    const isFlex = IsServiceFLEX(selectedService)
    const isDOT = isPoct ? undefined : selectedService.DOT

    let filteredTests = tests
    if (isPoct) {
      filteredTests = tests.filter((test) => test.POCT && !test.FLEX)
    } else if (isFlex) {
      filteredTests = tests.filter((test) => test.FLEX)
    } else if (isDOT) {
      filteredTests = tests.filter((test) => test.DOT)
    } else if (isDOT === false) {
      filteredTests = tests.filter((test) => test.NDOT)
    }

    if (sampleType) {
      filteredTests = filteredTests.filter((t) => t.SampleTypeCode === sampleType)
    }
    return filteredTests
  }

  groupTestsByService = (tests: TestToBePerform[]) => {
    const { orderableServices } = this.props

    return orderableServices.reduce((acc, service) => {
      const serviceCode = service.FFServiceCode as ServiceCode
      const testSampleType = [SampleType.SD, SampleType.SLD].includes(service.SampleType as SampleType)
        ? SampleType.SLD
        : (service.SampleType as SampleType)

      acc[serviceCode] = this.filterTests(tests, ServiceByCode(orderableServices, serviceCode), testSampleType)

      return acc
    }, {} as ServiceTests)
  }

  setAllServicesDisabled = (disabled: boolean) => {
    this.setState({ isAllServicesDisabled: disabled })
  }

  onChangeLab = (lab: LabAccount) => {
    this.setState({ ...this.defaultService }, () => this.labAccount$.next(lab))
  }

  onChangeTestType = (value: string) => {
    const { orderableServices } = this.props
    const serviceCode = value as ServiceCode
    const service = ServiceByCode(orderableServices, serviceCode)
    const isPOCT = IsServicePOCT(service)
    const isDOT = service.DOT
    const reqSpecimenCollection = isPOCT ? "single" : isDOT ? "split" : "single"
    const selectedTest = this.props.panelId
      ? this.state.availableTests.find((item) => item.PanelID === this.props.panelId)
      : undefined

    this.setState((prev) => ({
      serviceCode,
      isDOT,
      isMarketplace: service.Marketplace,
      reqSpecimenCollection,
      tests: prev.serviceTests[serviceCode] || [],
      testToPerform: selectedTest?.Description || "",
      testToPerformObj: selectedTest,
      selectedTestType: service.SampleType as SampleType,
      observedCollection: service.SampleType === SampleType.UR ? prev.observedCollection : false,
    }))
  }

  onChangeTest = (values: TestToBePerform[]) => {
    const { serviceTests, serviceCode, tests } = this.state

    const selected = values[0]
    if (!selected) return

    const selectedSampleType = selected.SampleTypeCode as SampleType

    const selectedServiceEntry = Object.entries(serviceTests).find(([, tests]) =>
      tests.some((test) => test.PanelID === selected.PanelID)
    )
    const selectedServiceCode = selectedServiceEntry?.[0] as ServiceCode

    const shouldUpdateServiceCode = !serviceCode && selectedServiceCode
    const newServiceCode = shouldUpdateServiceCode ? selectedServiceCode : serviceCode
    const newTests = selectedServiceCode ? serviceTests[selectedServiceCode] || tests : tests

    this.setState(
      {
        testToPerform: selected.Code || "",
        testToPerformObj: selected,
        selectedTestType: selectedSampleType,
        serviceCode: newServiceCode,
        tests: newTests,
      },
      () => {
        if (shouldUpdateServiceCode) {
          const { orderableServices } = this.props
          const { serviceCode } = this.state

          const service = ServiceByCode(orderableServices, serviceCode);
          const isDOT = service.DOT;
          const isPOCT = IsServicePOCT(service);
          const reqSpecimenCollection = isPOCT ? "single" : isDOT ? "split" : "single"

          this.setState((prev) => ({
            serviceCode,
            isDOT,
            isMarketplace: service.Marketplace,
            reqSpecimenCollection,
            selectedTestType: service.SampleType as SampleType,
            observedCollection: service.SampleType === SampleType.UR ? prev.observedCollection : false,
          }))
        }
      }
    )
  }

  onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.type === "checkbox" ? e.target.checked : e.target.value
    this.setState({ ...{}, [e.target.name]: value })
  }

  onServiceAdd = () => {
    const observedCollection = this.state.observedCollection || (this.props.isObservedMandatory && this.state.isDOT)
    this.props.onAdd({ ...this.state, observedCollection } as ServiceDataModel)
    this.setInitialState()
  }

  onPanelClose = () => {
    // Clear all entered values
    this.setInitialState()
  }

  setDefaultTests = (tests: TestToBePerform[]) => {
    if(!this.state.tests?.length) {
      this.setState({ tests })  
    }
  }

  isValid = (data: ServiceDataModel) => {
    if (this.state.isAllServicesDisabled || this.props.denyAddService) {
      return false
    }
    return (
      (IsServiceCodePOCT(data.serviceCode) || typeof data.isDOT === "boolean") && // POCT or DOT or Non-DOT type is set
      !!data.testToPerform &&
      data.testToPerform.length > 0 && // testToPerform is set
      !!data.labAccount &&
      data.labAccount.length > 0 && // labAccount is set
      (data.isDOT !== true || this.state.isValidMode)
    )
  }

  hasAvailableTests = () => {
    const { availableTests, labAccountObj } = this.state

    return labAccountObj && availableTests.length > 0
  }

  showSelectSampleTestType = () => {
    const { serviceCode, isDOT } = this.state

    return (
      this.hasAvailableTests() &&
      !isDOT &&
      ![ServiceCode.None, ServiceCode.POCT, ServiceCode.FLEX].includes(serviceCode)
    )
  }

  showSpecimenCollection = () => {
    const { selectedTestType } = this.state
    const showSelectSampleTestType = this.showSelectSampleTestType()

    return (
      (!showSelectSampleTestType && this.hasAvailableTests()) ||
      (showSelectSampleTestType && selectedTestType !== undefined)
    )
  }

  showObserved = () => {
    const { selectedTestType } = this.state
    const showSelectSampleTestType = this.showSelectSampleTestType()

    return (
      (!showSelectSampleTestType && this.hasAvailableTests()) ||
      (showSelectSampleTestType && selectedTestType === SampleType.UR)
    )
  }

  showDOTRelatedControls = () => {
    const { isDOT } = this.state

    return this.hasAvailableTests() && isDOT === true
  }

  selectServiceByDefault = () => {
    const { availableTests, serviceTests } = this.state
    const { panelId } = this.props

    const selectedTest = availableTests.find((item) => item.PanelID === panelId)

    const serviceCode = selectedTest
      ? this.getDefaultServiceByTest(selectedTest)
      : this.getDefaultService(serviceTests)

    if (serviceCode) {
      this.onChangeTestType(serviceCode)
    }
  }

  getDefaultServiceByTest = (selectedTest: TestToBePerform) => {
    const { DOT, SampleTypeCode, POCT, FLEX } = selectedTest
    const { sampleType, isDot } = this.props

    const sampleTypeCodeValue = (sampleType || SampleTypeCode) as SampleType
    const dotValue = sampleType ? isDot : DOT

    if (FLEX) {
      return ServiceCode.FLEX
    }

    if (POCT) {
      return ServiceCode.POCT
    }

    if (sampleTypeCodeValue === SampleType.UR) {
      return dotValue ? ServiceCode.DrugDOTUrine : ServiceCode.DrugNonDOTUrine
    }

    if ([SampleType.SD, SampleType.SLD].includes(sampleTypeCodeValue)) {
      return dotValue ? ServiceCode.DrugDOTOral : ServiceCode.DrugNonDOTOral
    }

    return ServiceCode.DrugNonDOTUrine
  }

  getDefaultService = (serviceTests: ServiceTests) => {
    // Check for FLEX service first
    if ((serviceTests[ServiceCode.FLEX]?.length ?? 0) > 0) {
      return ServiceCode.FLEX
    }
    const serviceTestsEntries = Object.entries(serviceTests)

    // Find a single service with tests while others have no tests
    const defaultService = serviceTestsEntries.find(
      ([serviceCode, tests]) =>
        tests.length > 0 && serviceTestsEntries.every(([s, t]) => s !== serviceCode && (t?.length ?? 0) === 0)
    )?.[0] as ServiceCode
    
    // If there is a single service with tests, use it
    if (defaultService) {
      return defaultService
    }

    // Determine if only DOT services have tests
    const hasOnlyDOTServices = !serviceTestsEntries.some(
      ([serviceCode, tests]) =>
        ![ServiceCode.DrugDOTUrine, ServiceCode.DrugDOTOral].includes(serviceCode as ServiceCode) &&
        (tests?.length ?? 0) > 0
    )

    if (hasOnlyDOTServices) {
      return this.getDefaultDOTServiceCode(serviceTests);
    }

    // Determine if only Non-DOT services have tests
    const hasOnlyNonDOTServices = !serviceTestsEntries.some(
      ([serviceCode, tests]) =>
        ![ServiceCode.DrugNonDOTUrine, ServiceCode.DrugNonDOTOral].includes(serviceCode as ServiceCode) &&
        (tests?.length ?? 0) > 0
    )
    if (hasOnlyNonDOTServices) {
      return this.getDefaultNonDOTServiceCode(serviceTests);
    }
    
    return undefined
  }

  getDefaultSampleType = (lookupCode: string): string | undefined => {
    return this.props.defaults?.find(
      (item) =>
        item.LookupCode === lookupCode && item.Show && item.IsDefault
    )?.FieldName;
  }

  getDefaultDOTServiceCode = (serviceTests: ServiceTests) => {
    const hasDrugDOTOral = serviceTests[ServiceCode.DrugDOTOral]?.length > 0
    const hasDrugDOTUrine = serviceTests[ServiceCode.DrugDOTUrine]?.length > 0

    const defaultDOTSampleType = this.getDefaultSampleType(DefaultLookupCode.DOTSampleType);

    if (defaultDOTSampleType === SampleType.SD && hasDrugDOTOral) {
      return ServiceCode.DrugDOTOral
    }

    if (defaultDOTSampleType === SampleType.UR && hasDrugDOTUrine) {
      return ServiceCode.DrugDOTUrine
    }

    return undefined
  }

  getDefaultNonDOTServiceCode = (serviceTests: ServiceTests) => {
    const hasDrugNonDOTOral = serviceTests[ServiceCode.DrugNonDOTOral]?.length > 0
    const hasDrugNonDOTUrine = serviceTests[ServiceCode.DrugNonDOTUrine]?.length > 0

    const defaultNonDOTSampleType = this.getDefaultSampleType(DefaultLookupCode.NonDOTSampleType);

    if (defaultNonDOTSampleType === SampleType.SD && hasDrugNonDOTOral) {
      return ServiceCode.DrugNonDOTOral
    }

    if (defaultNonDOTSampleType === SampleType.UR && hasDrugNonDOTUrine) {
      return ServiceCode.DrugNonDOTUrine
    }

    return undefined
  }

  render() {
    const { disabled, isObservedMandatory } = this.props
    const {
      labAccounts,
      labAccountObj,
      labAccountsLoading,
      availableTests,
      tests,
      testToPerformObj,
      testsLoading,
      labAccountCompanyName,
      isDOT,
      reqSpecimenCollection,
      observedCollection,
      serviceCode,
      serviceTests,
    } = this.state

    const observedCheckedDisabled = isObservedMandatory && isDOT

    return (
      <SelectServiceTogglePanel
        title="DRUG TEST"
        data={this.state as ServiceDataModel}
        collapsibleId="DrugTestCollapsePanel"
        disabled={disabled}
        open={this.props.open}
        onSubmit={this.onServiceAdd}
        onValidate={this.isValid}
        onClose={this.onPanelClose}
      >
        <Form.Row>
          <Col xs="12">
            <Form.Group>
              <Form.Label className="font-weight-bold required">LAB ACCOUNT</Form.Label>
              <Typeahead
                id="labAccountsTypeahead"
                labelKey="Description"
                filterBy={(option, props) => {
                  return (
                    props.selected.length > 0 ||
                    option.Description.toLowerCase().indexOf(props.text.toLowerCase()) !== -1
                  )
                }}
                onChange={(selected: LabAccount[]) => this.onChangeLab(selected[0])}
                options={labAccounts}
                isInvalid={!labAccountObj}
                disabled={!!this.props.labAccountNumber}
                placeholder="Enter the Lab Account name"
                selected={labAccountObj ? [labAccountObj] : []}
              />
            </Form.Group>
          </Col>
        </Form.Row>
        {labAccountCompanyName && (
          <Form.Row>
            <Col className="font-weight-bold form-group">{labAccountCompanyName}</Col>
          </Form.Row>
        )}
        {!labAccountsLoading && !testsLoading && labAccountObj && availableTests.length === 0 && (
          <Form.Row>
            <Col className="error-message">No services found for lab account</Col>
          </Form.Row>
        )}
        {testsLoading && (
          <Form.Row>
            <Col className="text-center">
              <Spinner animation="border" variant="secondary" />
            </Col>
          </Form.Row>
        )}
        {!labAccountsLoading && !testsLoading && this.hasAvailableTests() && (
          <Form.Row>
            <Col>
              <DrugServicesPanel
                {...this.props}
                selectedServiceCode={serviceCode}
                serviceTests={serviceTests}
                onChangeTestType={this.onChangeTestType}
                onUpdateTests={this.setDefaultTests}
                setAllServicesDisabled={this.setAllServicesDisabled}
              />
            </Col>
            <Col xs="1" md="1" sm="1">
              {isDOT && <Image src={dotImage} />}
            </Col>
          </Form.Row>
        )}
        {this.showSpecimenCollection() && (
          <Form.Row>
            <Col xs="12">
              <SelectSpecimenCollection
                selectedValue={reqSpecimenCollection}
                disabled={isDOT === true}
                onChange={this.onChange}
              />
            </Col>
          </Form.Row>
        )}
        {this.showObserved() && (
          <Form.Row>
            <Col xs="12">
              <SelectObserved
                checked={observedCheckedDisabled || observedCollection}
                disabled={observedCheckedDisabled}
                onChange={this.onChange}
              />
            </Col>
          </Form.Row>
        )}
        {this.showDOTRelatedControls() && (
          <RegulatoryModeControl
            defaults={this.props.defaults}
            regulatoryModeID={this.props.regulatoryModeID}
            employeeCategoryID={this.props.employeeCategoryID}
            onChange={(isValid, mode, cat) =>
              this.setState({ isValidMode: isValid, regulatoryModeObj: mode, categoryObj: cat })
            }
          />
        )}
        {this.hasAvailableTests() && (
          <Form.Row>
            <Col xs="12">
              <Form.Group>
                <Form.Label className="font-weight-bold required">TEST TO BE PERFORMED</Form.Label>
                <Typeahead
                  id="drugTestToPerformTypeahead"
                  labelKey="Description"
                  filterBy={(option, props) => {
                    return (
                      props.selected.length > 0 ||
                      option.Description.toLowerCase().indexOf(props.text.toLowerCase()) !== -1
                    )
                  }}
                  onChange={this.onChangeTest}
                  isInvalid={!testsLoading && !testToPerformObj}
                  options={tests}
                  disabled={testsLoading || tests.length === 0 || !!this.props.panelId}
                  placeholder= {testsLoading ? "Loading..." : tests.length > 0 ? "Enter the Test name" : "No Tests Available"}
                  selected={testToPerformObj ? [testToPerformObj as TestToBePerform] : []}
                />
              </Form.Group>
            </Col>
          </Form.Row>
        )}
      </SelectServiceTogglePanel>
    )
  }
}
